"use client";

import { useLicenseContext } from "@/app/_contexts/LicenseProvider";
import {
  createAreaModuleAvailability,
  getAvailableAreasFromModules,
  getDefaultAreaFromModules,
} from "@/app/_services/AreaAccess/areaAccess";
import {
  AreaKey,
  findAreaByPath,
} from "@/app/_utils/navigation/areaRouteMap";
import { usePathname } from "@/i18n/navigation";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

type AreaContextValue = {
  activeArea: AreaKey;
  setActiveArea: (area: AreaKey) => void;
  availableAreas: AreaKey[];
};

const AreaContext = createContext<AreaContextValue | undefined>(undefined);

export function AreaProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const { hasModule } = useLicenseContext();
  const areaModuleAvailability = useMemo(
    () => createAreaModuleAvailability(hasModule),
    [hasModule]
  );

  const defaultArea = useMemo(
    () => getDefaultAreaFromModules(areaModuleAvailability),
    [areaModuleAvailability]
  );

  const availableAreas = useMemo(
    () => getAvailableAreasFromModules(areaModuleAvailability),
    [areaModuleAvailability]
  );

  const hasArea = useCallback(
    (area: AreaKey) => availableAreas.includes(area),
    [availableAreas]
  );

  const [activeArea, setActiveAreaState] = useState<AreaKey>(defaultArea);

  useEffect(() => {
    if (!hasArea(activeArea)) {
      setActiveAreaState(defaultArea);
    }
  }, [activeArea, defaultArea, hasArea]);

  useEffect(() => {
    const areaFromPath = findAreaByPath(pathname);
    if (!areaFromPath || !hasArea(areaFromPath)) {
      return;
    }
    setActiveAreaState((previous) =>
      previous === areaFromPath ? previous : areaFromPath
    );
  }, [hasArea, pathname]);

  const setActiveArea = useCallback(
    (area: AreaKey) => {
      if (!hasArea(area)) {
        return;
      }
      setActiveAreaState(area);
    },
    [hasArea]
  );

  const value = useMemo(
    () => ({ activeArea, setActiveArea, availableAreas }),
    [activeArea, availableAreas, setActiveArea]
  );

  return <AreaContext.Provider value={value}>{children}</AreaContext.Provider>;
}

export function useAreaContext() {
  const context = useContext(AreaContext);
  if (!context) {
    throw new Error("useAreaContext must be used within an AreaProvider");
  }
  return context;
}
